import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "DetalleMatricula";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarMatriculados = () => {
  const [detalles, setDetalles] = useState([]);
  const [cargando, setCargando] = useState(false);

  const navigate = useNavigate();
  const { id: matriculaId } = useParams();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloDetalleMatricula = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloDetalleMatricula = useMemo(() => getModuloDetalleMatricula(), []);

  const puedeLeer =
    moduloDetalleMatricula?.puedeLeer === true ||
    moduloDetalleMatricula?.leer === true ||
    moduloDetalleMatricula?.ver === true ||
    moduloDetalleMatricula?.listar === true ||
    moduloDetalleMatricula?.read === true ||
    moduloDetalleMatricula?.view === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const formatearFecha = (fecha) => {
    if (!fecha) return "N/A";

    const fechaObj = new Date(fecha);

    if (Number.isNaN(fechaObj.getTime())) return "N/A";

    return fechaObj.toLocaleDateString("es-CO");
  };

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setDetalles([]);
      return;
    }

    setCargando(true);

    try {
      const { data } = await axios.get(
        `/api/detallematricula/listbyid/${matriculaId}`,
        authConfig()
      );

      if (data?.ok && Array.isArray(data.detalles)) {
        setDetalles(data.detalles);
      } else {
        setDetalles([]);
      }
    } catch (error) {
      console.error("Error al cargar los matriculados:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || error.message,
        "error"
      );

      setDetalles([]);
    } finally {
      setCargando(false);
    }
  }, [matriculaId, puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    if (matriculaId) getData();
  }, [matriculaId, getData]);

  const badgeEstado = (estado = "") => {
    const estadoNormalizado = estado.toLowerCase();

    if (estadoNormalizado.includes("activo")) {
      return "bg-success-subtle text-success border border-success-subtle";
    }

    if (estadoNormalizado.includes("desert")) {
      return "bg-danger-subtle text-danger border border-danger-subtle";
    }

    if (estadoNormalizado.includes("traslado")) {
      return "bg-warning-subtle text-warning border border-warning-subtle";
    }

    return "bg-secondary-subtle text-secondary border border-secondary-subtle";
  };

  if (!puedeLeer) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de lectura
              </h5>

              <p className="mb-4">
                No tienes permisos para visualizar los detalles de matrícula.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Lista de matriculados
                </h5>

                <small className="text-secondary">
                  Vista tipo carnet de los aprendices asociados.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={cargando}
                >
                  <i
                    className={`fa-solid ${cargando ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {cargando ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status"></div>
                <div className="mt-3">Cargando matriculados…</div>
              </div>
            ) : detalles.length > 0 ? (
              <div className="row g-3">
                {detalles.map((detalle) => {
                  const aprendiz = detalle.aprendizObjectId || {};

                  const nombreCompleto =
                    detalle.nombreAprendiz ||
                    [
                      aprendiz.nombre1Aprendiz,
                      aprendiz.nombre2Aprendiz,
                      aprendiz.apellido1Aprendiz,
                      aprendiz.apellido2Aprendiz,
                    ]
                      .filter(Boolean)
                      .join(" ") ||
                    "Sin nombre registrado";

                  const documento =
                    detalle.documentoAprendiz ||
                    aprendiz.documentoAprendiz ||
                    "N/A";

                  const tipoDocumento = aprendiz.tipodocumentoAprendiz || "N/A";
                  const telefono =
                    aprendiz.telefono1Aprendiz ||
                    aprendiz.telefono2Aprendiz ||
                    "N/A";
                  const email = aprendiz.emailAprendiz || "N/A";
                  const rh = aprendiz.rhAprendiz || "N/A";
                  const sexo = aprendiz.sexoAprendiz || "N/A";
                  const ciudad =
                    aprendiz.ciudadAprendiz || detalle.ciudadAprendiz || "N/A";
                  const fechaNacimiento = formatearFecha(
                    aprendiz.fechanacimientoAprendiz
                  );
                  const imagenAprendiz =
                    aprendiz.imgAprendiz || detalle.imgAprendiz || "";

                  return (
                    <div
                      className="col-12 col-sm-6 col-lg-4 col-xl-3"
                      key={detalle._id}
                    >
                      <div className="card h-100 border rounded-4 shadow-sm overflow-hidden">
                        <div className="text-center bg-body-tertiary border-bottom p-2 p-sm-3">
                          {imagenAprendiz ? (
                            <img
                              src={imagenAprendiz}
                              alt={nombreCompleto}
                              className="rounded-circle border shadow-sm bg-white"
                              style={{
                                width: "clamp(95px, 28vw, 135px)",
                                height: "clamp(95px, 28vw, 135px)",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-white border shadow-sm mx-auto d-flex align-items-center justify-content-center fw-bold text-secondary"
                              style={{
                                width: "clamp(95px, 28vw, 135px)",
                                height: "clamp(95px, 28vw, 135px)",
                                fontSize: "clamp(28px, 8vw, 40px)",
                              }}
                            >
                              {nombreCompleto.charAt(0).toUpperCase()}
                            </div>
                          )}

                          <h6
                            className="fw-bold mb-1 mt-3 text-break"
                            style={{
                              lineHeight: "1.2",
                              minHeight: "38px",
                            }}
                            title={nombreCompleto}
                          >
                            {nombreCompleto}
                          </h6>

                          <div className="small text-secondary mb-2 text-break">
                            {tipoDocumento}: {documento}
                          </div>

                          <span
                            className={`badge rounded-pill px-3 py-2 ${badgeEstado(
                              detalle.estadoMatricula
                            )}`}
                          >
                            {detalle.estadoMatricula || "Sin estado"}
                          </span>
                        </div>

                        <div className="card-body p-2 p-sm-3">
                          <div className="small">
                            <div className="d-flex justify-content-between gap-2 border-bottom py-1">
                              <span className="text-secondary flex-shrink-0">Ficha</span>
                              <strong className="text-end text-break">
                                {detalle.fichaGrupo || "N/A"}
                              </strong>
                            </div>

                            <div className="d-flex justify-content-between gap-2 border-bottom py-1">
                              <span className="text-secondary flex-shrink-0">Tipo</span>
                              <strong className="text-end text-break">
                                {detalle.tipoMatricula || "N/A"}
                              </strong>
                            </div>

                            <div className="d-flex justify-content-between gap-2 border-bottom py-1">
                              <span className="text-secondary flex-shrink-0">RH</span>
                              <strong className="text-end text-break">{rh}</strong>
                            </div>

                            <div className="d-flex justify-content-between gap-2 border-bottom py-1">
                              <span className="text-secondary flex-shrink-0">Sexo</span>
                              <strong className="text-end text-break">{sexo}</strong>
                            </div>

                            <div className="d-flex justify-content-between gap-2 border-bottom py-1">
                              <span className="text-secondary flex-shrink-0">Ciudad</span>
                              <strong className="text-end text-break">{ciudad}</strong>
                            </div>

                            <div className="border-bottom py-1">
                              <span className="text-secondary d-block">Teléfono</span>
                              <strong className="text-break d-block">{telefono}</strong>
                            </div>

                            <div className="border-bottom py-1">
                              <span className="text-secondary d-block">Correo</span>
                              <strong className="text-break d-block">{email}</strong>
                            </div>

                            <div className="py-1">
                              <span className="text-secondary d-block">Fecha nacimiento</span>
                              <strong className="text-break d-block">{fechaNacimiento}</strong>
                            </div>
                          </div>

                          <div className="d-flex justify-content-center justify-content-md-start gap-2 flex-wrap border-top mt-3 pt-3">
                            {/* botones opcionales */}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🎓</div>
                <div className="fw-semibold">No se encontraron registros</div>
                <div className="small">
                  No hay aprendices asociados a esta matrícula.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarMatriculados;